import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type PolicyType = 'terms' | 'privacy' | null;
export function Footer() {
  const [activePolicy, setActivePolicy] = useState<PolicyType>(null);
  const togglePolicy = (policy: PolicyType) => {
    setActivePolicy(prev => prev === policy ? null : policy);
  };
  return <footer className="border-t border-[#1B2A4A]/10 bg-[#FAF8F5]" data-id="element-50">
      <div className="max-w-[680px] mx-auto px-6 py-10 pb-28 md:pb-10" data-id="element-51">
        {/* Disclaimer */}
        <div className="mb-8 p-5 bg-[#1B2A4A]/[0.03] rounded border border-[#1B2A4A]/10" data-id="element-52">
          <p className="text-xs text-[#1B2A4A]/50 leading-relaxed" data-id="element-53">
            <span className="font-bold text-[#1B2A4A]/60 uppercase tracking-wide text-[10px]" data-id="element-54">
              Disclaimer:
            </span>{' '}
            This guide is for informational and educational purposes only. It
            does not constitute financial, legal, or tax advice. The information
            provided is based on publicly available Social Security
            Administration rules and regulations as of the date of publication.
            Individual results may vary depending on your specific
            circumstances, claiming history, and eligibility. We strongly
            recommend consulting with a qualified financial advisor or
            contacting the SSA directly before making any changes to your
            benefits. The figures and examples used throughout this page are
            illustrative and may not reflect your actual benefit amounts. Past
            results do not guarantee future outcomes. This product is not
            affiliated with, endorsed by, or sponsored by the Social Security
            Administration or any government agency.
          </p>
        </div>

        {/* Links */}
        <div className="flex items-center justify-center gap-4 mb-6 text-sm font-sans" data-id="element-55">
          <button onClick={() => togglePolicy('terms')} className={`underline underline-offset-2 transition-colors duration-200 ${activePolicy === 'terms' ? 'text-[#D4652A]' : 'text-[#1B2A4A]/50 hover:text-[#1B2A4A]/80'}`} data-id="element-56">
            Terms of Service
          </button>
          <span className="text-[#1B2A4A]/20" data-id="element-57">|</span>
          <button onClick={() => togglePolicy('privacy')} className={`underline underline-offset-2 transition-colors duration-200 ${activePolicy === 'privacy' ? 'text-[#D4652A]' : 'text-[#1B2A4A]/50 hover:text-[#1B2A4A]/80'}`} data-id="element-58">
            Privacy Policy
          </button>
        </div>

        {/* Expandable Policy Content */}
        <AnimatePresence data-id="element-59">
          {activePolicy && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden mb-6" data-id="element-60">
              <div className="bg-white border border-[#1B2A4A]/10 rounded-lg p-6 md:p-8 relative" data-id="element-61">
                <button onClick={() => setActivePolicy(null)} className="absolute top-4 right-4 text-[#1B2A4A]/40 hover:text-[#1B2A4A]/70 transition-colors" aria-label="Close" data-id="element-62">
                  <XIcon className="w-5 h-5" data-id="element-63" />
                </button>

                {activePolicy === 'terms' && <div className="space-y-4 text-sm text-[#1B2A4A]/70 leading-relaxed pr-6" data-id="element-64">
                    <h4 className="text-lg font-bold text-[#1B2A4A] mb-4" data-id="element-65">
                      Terms of Service
                    </h4>

                    <p data-id="element-66">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-67">
                        1. Acceptance of Terms.
                      </span>{' '}
                      By purchasing and/or using "The Social Security Claiming
                      Mistake Rescue" guide, you agree to be bound by these
                      Terms of Service. If you do not agree, do not purchase or
                      use this product.
                    </p>

                    <p data-id="element-68">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-69">
                        2. Product Description.
                      </span>{' '}
                      This product is a digital PDF guide containing educational
                      information about Social Security claiming strategies. It
                      is delivered electronically upon purchase.
                    </p>

                    <p data-id="element-70">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-71">
                        3. No Professional Advice.
                      </span>{' '}
                      The content of this guide is for informational purposes
                      only and does not constitute financial, legal, or tax
                      advice. You should consult with qualified professionals
                      before making decisions about your Social Security
                      benefits.
                    </p>

                    <p data-id="element-72">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-73">
                        4. No Guarantees of Results.
                      </span>{' '}
                      While the strategies described are based on actual SSA
                      rules and regulations, individual results will vary. We do
                      not guarantee any specific outcome or increase in
                      benefits.
                    </p>

                    <p data-id="element-74">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-75">
                        5. Refund Policy.
                      </span>{' '}
                      We offer a 30-day money-back guarantee. If you are not
                      satisfied with the guide for any reason, contact us within
                      30 days of purchase for a full refund. No questions asked.
                    </p>

                    <p data-id="element-76">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-77">
                        6. Intellectual Property.
                      </span>{' '}
                      All content in this guide is protected by copyright. You
                      may not reproduce, distribute, or share this guide without
                      written permission.
                    </p>

                    <p data-id="element-78">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-79">
                        7. Limitation of Liability.
                      </span>{' '}
                      To the maximum extent permitted by law, we shall not be
                      liable for any indirect, incidental, or consequential
                      damages arising from the use of this guide.
                    </p>

                    <p data-id="element-80">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-81">
                        8. Changes to Terms.
                      </span>{' '}
                      We reserve the right to modify these terms at any time.
                      Continued use of the product constitutes acceptance of
                      updated terms.
                    </p>
                  </div>}

                {activePolicy === 'privacy' && <div className="space-y-4 text-sm text-[#1B2A4A]/70 leading-relaxed pr-6" data-id="element-82">
                    <h4 className="text-lg font-bold text-[#1B2A4A] mb-4" data-id="element-83">
                      Privacy Policy
                    </h4>

                    <p data-id="element-84">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-85">
                        1. Information We Collect.
                      </span>{' '}
                      When you purchase our guide, we collect your name, email
                      address, and payment information necessary to process your
                      order. We do not collect Social Security numbers or
                      benefit information.
                    </p>

                    <p data-id="element-86">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-87">
                        2. How We Use Your Information.
                      </span>{' '}
                      Your information is used solely to process your purchase,
                      deliver the digital product, and communicate with you
                      about your order. We may also send occasional updates
                      related to Social Security strategies if you opt in.
                    </p>

                    <p data-id="element-88">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-89">
                        3. Payment Security.
                      </span>{' '}
                      All payment transactions are processed through secure,
                      encrypted third-party payment processors. We do not store
                      your credit card information on our servers.
                    </p>

                    <p data-id="element-90">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-91">
                        4. No Sharing of Personal Data.
                      </span>{' '}
                      We do not sell, trade, or rent your personal information
                      to third parties. We may share information only as
                      required by law or to protect our legal rights.
                    </p>

                    <p data-id="element-92">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-93">
                        5. Cookies.
                      </span>{' '}
                      Our website may use cookies to improve your browsing
                      experience and analyze site traffic. You can disable
                      cookies in your browser settings at any time.
                    </p>

                    <p data-id="element-94">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-95">
                        6. Data Retention.
                      </span>{' '}
                      We retain your purchase information for as long as
                      necessary to fulfill our obligations, including refund
                      processing and customer support.
                    </p>

                    <p data-id="element-96">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-97">
                        7. Your Rights.
                      </span>{' '}
                      You have the right to request access to, correction of, or
                      deletion of your personal data. Contact us at any time to
                      exercise these rights.
                    </p>

                    <p data-id="element-98">
                      <span className="font-bold text-[#1B2A4A]/80" data-id="element-99">
                        8. Contact.
                      </span>{' '}
                      For any privacy-related questions or concerns, please
                      reach out to us through our customer support email.
                    </p>
                  </div>}
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Copyright */}
        <p className="text-center text-[#1B2A4A]/40 text-xs" data-id="element-100">
          © {new Date().getFullYear()} Social Security Claiming Mistake Rescue.
          All rights reserved.
        </p>
      </div>
    </footer>;
}